import { SearchPageType } from "~/Search/useSearchPageType";
import { userIsCustomer } from "~/utils/roles";
import {
  type FacetFilterData,
  Filter,
  type FilterConfig,
  type FilterGroupData,
  type TechValueFilterCategoryData,
} from "~/Search/filterConfigModels";

export type GetFilterConfigApiResult = {
  filterGroups: FilterConfig;
  filterGroupsImperial: FilterConfig;
  tabularViewCols: string[];
  tabularViewColsImperial: string[];
};

export type FilterGroupDTO = TechFilterGroupDTO | FacetFilterGroupDTO | TaxonomyFilterGroupDTO;

interface TechFilterGroupDTO {
  title: string | null;
  maxElementsVisible: number;
  type: typeof Filter.TECH_VALUE;
  items: TechFilterCategoryDTO[];
}

interface TechFilterCategoryDTO {
  title: string;
  facetId: null;
  columnAmount: number;
  initiallyExpanded: boolean;
  maxElementsVisible: number;
  items: TechFilterDTO[];
}

interface TechFilterDTO {
  uid: string;
  name: string;
  assistantLabel: string;
  groupName: string;
  sequenceId: number;
  uom: string;
  sortFields: { uid: string; ascending: boolean }[];
  abbreviation: string;
  description?: string;
  unit?: string;
  visibleOnFilters?: boolean;
}

interface FacetFilterGroupDTO {
  title: string | null;
  maxElementsVisible: number;
  type: typeof Filter.FACET;
  items: FacetFilterDTO[];
}

interface FacetFilterDTO {
  title: string;
  facetId: string;
  columnAmount: number;
  initiallyExpanded: boolean;
}

interface TaxonomyFilterGroupDTO {
  title: string | null;
  type: typeof Filter.TAXONOMY;
  showEmptyCategories: boolean;
  initiallyExpanded?: boolean;
}

export function transformFilterConfig(groups: FilterGroupDTO[], pageType?: SearchPageType): FilterConfig {
  const list = groups.flatMap(transformFilterConfigItem);

  if (userIsCustomer() && pageType !== undefined) {
    if (pageType === SearchPageType.Campaign && window.SCHA.feature.showRelevantToggle) {
      list.unshift({
        type: Filter.RELEVANT,
      });
    }

    if (pageType !== SearchPageType.Campaign) {
      const taxonomyIndex = list.findIndex((group) => group.type === Filter.TAXONOMY);
      if (taxonomyIndex !== -1) {
        list.splice(taxonomyIndex + 1, 0, {
          type: Filter.SALES,
        });
      }
    }
  }

  // On PLP we remove the taxonomy filter
  if (pageType === SearchPageType.PLP) {
    const taxonomyIndex = list.findIndex((group) => group.type === Filter.TAXONOMY);
    if (taxonomyIndex !== -1) {
      list.splice(taxonomyIndex, 1);
    }
  }

  return list;
}

function transformFilterConfigItem(dto: FilterGroupDTO): FilterGroupData[] {
  if (dto.type === Filter.TECH_VALUE) {
    return transformTechFilterGroup(dto);
  }
  if (dto.type === Filter.FACET) {
    return transformFacetFilterGroup(dto);
  }
  if (dto.type === Filter.TAXONOMY) {
    return [{ ...dto, categories: [] }];
  }
  throw new Error(`Not implemented!`);
}

function transformTechFilterGroup(dto: TechFilterGroupDTO): TechValueFilterCategoryData[] {
  return dto.items.map((categoryDto) => ({
    type: Filter.TECH_VALUE,
    title: categoryDto.title,
    facetId: categoryDto.facetId,
    maxElementsVisible: categoryDto.maxElementsVisible || undefined,
    initiallyExpanded: categoryDto.initiallyExpanded,
    items: categoryDto.items.map((techFilterDto) => {
      const ascCodeObj = techFilterDto.sortFields?.filter((item) => item.ascending)[0];
      const sortAscCode = ascCodeObj ? ascCodeObj.uid : undefined;
      const descCodeObj = techFilterDto.sortFields?.filter((item) => !item.ascending)[0];
      const sortDescCode = descCodeObj ? descCodeObj.uid : undefined;
      return {
        uid: techFilterDto.uid,
        title: `${techFilterDto.name}`,
        unit: `${!techFilterDto.uom ? "" : ` (${techFilterDto.uom})`}`,
        assistantLabel: techFilterDto.assistantLabel,
        tabularTitle: `${techFilterDto.abbreviation}`,
        sortAscCode,
        sortDescCode,
        description: techFilterDto.description === techFilterDto.name ? "" : techFilterDto.description,
        visibleOnFilters: techFilterDto.visibleOnFilters,
      };
    }),
  }));
}

function transformFacetFilterGroup(dto: FacetFilterGroupDTO): FacetFilterData[] {
  return dto.items.map((facetFilterDto) => ({
    type: Filter.FACET,
    title: facetFilterDto.title,
    code: facetFilterDto.facetId,
    columnAmount: facetFilterDto.columnAmount || 1,
    initiallyExpanded: facetFilterDto.initiallyExpanded,
  }));
}
